import type { Card, CompletedHandSummary, HandState, PokerMatchState, Side } from '../games/heads-up-poker';
import type { Role } from '../proto';

type Obj = Record<string, unknown>;
const invalid = (): never => { throw new Error('保存対局のPokerデータを確認できません'); };
const object = (value: unknown): Obj => value != null && typeof value === 'object' && !Array.isArray(value) ? value as Obj : invalid();
const number = (value: unknown): number => typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : invalid();
const side = (value: unknown): Side => value === 'First' || value === 'Second' ? value : invalid();
const seat = (value: unknown): 'sb' | 'bb' => value === 'sb' || value === 'bb' ? value : invalid();
const pair = (value: unknown): [number, number] => Array.isArray(value) && value.length === 2 ? [number(value[0]), number(value[1])] : invalid();
const cards = (value: unknown, max: number): Card[] => {
  if (!Array.isArray(value) || value.length > max) return invalid();
  return value.map(value => { const card = object(value); const rank = number(card.rank); const suit = number(card.suit); if (rank < 2 || rank > 14 || suit > 3) return invalid(); return {rank, suit}; });
};
const hole = (value: unknown): [Card, Card] | null => {
  if (value === null) return null;
  const decoded = cards(value, 2);
  return decoded.length === 2 ? [decoded[0], decoded[1]] : invalid();
};
const holes = (value: unknown): [[Card, Card] | null, [Card, Card] | null] => Array.isArray(value) && value.length === 2 ? [hole(value[0]), hole(value[1])] : invalid();
const winner = (value: unknown): ('sb' | 'bb')[] | null => value === null ? null : Array.isArray(value) && value.length <= 2 ? value.map(seat) : invalid();

function summary(value: unknown): CompletedHandSummary | null {
  if (value == null) return null;
  const src = object(value);
  return {hand_number: number(src.hand_number), winner: winner(src.winner) ?? [], pot: number(src.pot), board: cards(src.board, 5), hole: holes(src.hole), dealer_side_at_time: side(src.dealer_side_at_time)};
}

/** Typed recipient allowlist mirrors the engine projection and never aliases internal saved cards. */
export function projectLocalPokerState(value: unknown, viewer: Side | null): Omit<PokerMatchState, 'deck_seed'> & {deck_seed: string} {
  const src = object(value);
  const h = object(src.current_hand);
  const phase = h.phase;
  const awaiting = h.awaiting_cards;
  if (!['preflop', 'flop', 'turn', 'river', 'all_in_run_out', 'showdown', 'hand_complete', 'match_complete'].includes(String(phase)) || !(awaiting === null || ['holes', 'flop', 'turn', 'river', 'all_in_run_out'].includes(String(awaiting)))) return invalid();
  const dealer = side(src.current_dealer_side);
  const completed = summary(src.last_completed_hand);
  const handNumber = number(h.hand_number);
  const terminal = phase === 'hand_complete' || phase === 'match_complete';
  const publicSummary = terminal && completed?.hand_number === handNumber ? completed : null;
  const own = viewer == null ? null : viewer === dealer ? 0 : 1;
  const currentHoles = holes(h.hole);
  for (const index of [0, 1]) if (index !== own) currentHoles[index] = publicSummary?.hole[index] ?? null;
  const acted = h.acted_this_round;
  if (!Array.isArray(acted) || acted.length !== 2 || acted.some(value => typeof value !== 'boolean')) return invalid();
  const hand: HandState = {
    hand_number: handNumber, dealer: seat(h.dealer), phase: phase as HandState['phase'], stacks: pair(h.stacks), pot: number(h.pot), current_round_bet: pair(h.current_round_bet), min_raise_step: number(h.min_raise_step), last_aggressor: h.last_aggressor === null ? null : seat(h.last_aggressor), to_act: seat(h.to_act), blinds: pair(h.blinds), bb_ante: number(h.bb_ante), acted_this_round: [acted[0], acted[1]], hole: currentHoles, board: cards(h.board, 5), awaiting_cards: awaiting as HandState['awaiting_cards'], winner: winner(h.winner), pot_split: h.pot_split === null ? null : pair(h.pot_split), showdown_score: terminal && h.showdown_score !== null ? pair(h.showdown_score) : null,
  };
  const schedule = object(src.blind_schedule);
  if (!Array.isArray(schedule.levels)) return invalid();
  const blindSchedule = {hands_per_level: number(schedule.hands_per_level), levels: schedule.levels.map(value => { const level = object(value); return {sb: number(level.sb), bb: number(level.bb), bb_ante: number(level.bb_ante)}; })};
  let outcome: PokerMatchState['outcome'] = null;
  if (src.outcome !== null) { const decisive = object(object(src.outcome).decisive); outcome = {decisive: {winner: side(decisive.winner), hands_played: number(decisive.hands_played)}}; }
  return {starting_stack: number(src.starting_stack), blind_schedule: blindSchedule, stacks: pair(src.stacks), current_hand: hand, current_dealer_side: dealer, initial_dealer_side: side(src.initial_dealer_side), outcome, completed_hands: number(src.completed_hands), last_completed_hand: completed, deck_seed: '0000000000000000'};
}

export function canonicalLocalPokerMove(move: unknown): unknown {
  if (typeof move === 'string' && ['fold', 'check', 'call', 'all_in', 'next_hand'].includes(move)) return move;
  const src = object(move);
  if ('provide_my_hole' in src) { const body = object(src.provide_my_hole); return {provide_my_hole: {seat: seat(body.seat), cards: []}}; }
  if ('provide_board_cards' in src) return {provide_board_cards: {cards: cards(object(src.provide_board_cards).cards, 5)}};
  if ('bet' in src) return {bet: {amount: number(object(src.bet).amount)}};
  if ('raise' in src) return {raise: {to_total: number(object(src.raise).to_total)}};
  return invalid();
}

export function projectLocalPokerSnapshot(value: unknown, role: Role): unknown {
  const copy = structuredClone(value);
  const state = object(copy);
  if (!Array.isArray(state.boards)) return invalid();
  const hostSeat = state.host_seat ?? 'Leader';
  const matchSeat = role === 'spectator' ? null : role === 'host' ? hostSeat : hostSeat === 'Leader' ? 'Follower' : 'Leader';
  for (const value of state.boards) {
    const board = object(value);
    if (board.game_id !== 'HeadsUpPoker') continue;
    const viewer = matchSeat == null ? null : matchSeat === (board.first_seat ?? 'Leader') ? 'First' : 'Second';
    const projected = projectLocalPokerState(board.state, viewer);
    board.state = projected;
    board.outcome = projected.outcome;
    if (Array.isArray(board.history)) board.history = board.history.map(value => { const item = object(value); return {mv: canonicalLocalPokerMove(item.mv), side: side(item.side), ply: number(item.ply), at_ms: number(item.at_ms)}; });
    if (Array.isArray(board.legal)) board.legal = board.legal.map(canonicalLocalPokerMove);
    if (board._view != null) { const view = object(board._view); if (Array.isArray(view.legal)) view.legal = view.legal.map(canonicalLocalPokerMove); }
  }
  return copy;
}

export interface LocalPokerHandCache {hand_number: number; sbHole: [Card, Card] | null; bbHole: [Card, Card] | null; board: Card[]}
export function localPokerCacheFromState(value: unknown): LocalPokerHandCache {
  const hand = object(object(value).current_hand);
  const privateHoles = holes(hand.hole);
  return {hand_number: number(hand.hand_number), sbHole: privateHoles[0], bbHole: privateHoles[1], board: cards(hand.board, 5)};
}
/** Only the internal engine input is restored; recipient projections always run afterward. */
export function restoreLocalPokerCache(value: unknown, cache: LocalPokerHandCache): void {
  const hand = object(object(value).current_hand);
  if (hand.hand_number !== cache.hand_number) return;
  hand.hole = [hole(cache.sbHole), hole(cache.bbHole)];
  if (Array.isArray(hand.board) && cache.board.length > hand.board.length) hand.board = cards(cache.board, 5);
}
